import { useEffect } from "react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import CTABlock from "@/components/sections/CTABlock";
import TeamGrid from "@/components/team/TeamGrid";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import heroImage from "@/assets/bg-exterior-1.jpg";
import realtorImage from "@/assets/about-realtor.jpg";

const Team = () => {
  const { members } = useTeamMembers({ featuredOnly: true });
  useEffect(() => {
    document.title = "Meet the Team | Hadassah Homes Real Estate & Design";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Meet the Hadassah Homes team serving Massachusetts buyers, sellers, investors, and property owners with real estate, design, and property preparation guidance."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/team";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Meet the Team"
        title="Meet the Hadassah Homes Team"
        subtitle="Meet the professionals behind Hadassah Homes, serving buyers, sellers, investors, and property owners across Massachusetts with clarity, care, and strategy."
        backgroundImage={heroImage}
      />

      <ContentSection className="pt-18 pb-20 md:pt-22 md:pb-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-headline text-3xl leading-tight text-foreground md:text-5xl">
            Our Team
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Get to know the advisors guiding Hadassah Homes clients through real estate, design, investment, and property preparation decisions.
          </p>
        </div>
        <div className="mt-12 md:mt-14">
          <TeamGrid members={members} className="max-w-4xl gap-y-12" />
        </div>
      </ContentSection>

      <ContentSection variant="light">
        <div className="flex flex-col gap-12 md:flex-row md:items-center md:gap-20">
          <div className="w-full space-y-5 md:w-7/12">
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">
              Our Standard
            </span>
            <h2 className="font-headline text-3xl leading-tight text-foreground md:text-5xl">
              More Than Agents —<br className="hidden md:block" /> We Are REALTORS®
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              At Hadassah Homes, we are more than just agents — we are REALTORS®, bound by a higher standard of ethics, integrity, and commitment to our clients and communities.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              Being a REALTOR® means holding ourselves accountable to more than just transactions. It means advocating for fairness, honesty, and professionalism in every interaction. It means ensuring that every client — regardless of background or circumstance — is treated with respect and given the knowledge and resources to make empowered decisions.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              Our team stands for education, empowerment, and elevation. We believe real estate is more than buying or selling property — it&apos;s about creating pathways to opportunity, building generational wealth, and strengthening the communities we serve.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              Every REALTOR® on our team embraces the Realtor&apos;s Code of Ethics and embodies the Hadassah Homes mission:
            </p>
            <blockquote className="border-l-2 border-accent pl-6">
              <p className="font-headline text-xl italic leading-snug text-foreground md:text-2xl">
                &ldquo;To educate, empower, and elevate every individual we encounter — because every moment is a teaching and learning moment.&rdquo;
              </p>
            </blockquote>
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              When you work with a Hadassah Homes REALTOR®, you&apos;re not just getting representation — you&apos;re gaining a trusted partner who cares about your growth, your goals, and your future.
            </p>
          </div>
          <div className="w-full md:w-5/12">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={realtorImage}
                alt="REALTOR guidance and homeownership support"
                className="h-full w-full object-cover"
                loading="lazy"
                width={1280}
                height={960}
              />
            </div>
          </div>
        </div>
      </ContentSection>

      <CTABlock
        variant="accent"
        title="Ready to Take the Next Step?"
        description="Whether you are buying, selling, investing, or preparing a property, our team is here to guide you with clarity and care."
        primaryCta={{ text: "Book a Consultation →", href: "/consultation" }}
      />
    </SiteLayout>
  );
};

export default Team;
